import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const STATUS_CLS = {
  ELIGIBLE: "text-emerald-400 border-emerald-800", PASS: "text-emerald-400 border-emerald-800",
  AWARDED: "text-emerald-400 border-emerald-800", HIGH: "text-emerald-400 border-emerald-800",
  WATCH: "text-amber-300 border-amber-800", MODERATE: "text-amber-300 border-amber-800",
  REDUCED_SIZE: "text-amber-300 border-amber-800", RESTRICTED: "text-amber-300 border-amber-800",
  LOW: "text-amber-300 border-amber-800", DEGRADED: "text-amber-300 border-amber-800",
  FAIL: "text-red-400 border-red-800", BLOCKED: "text-red-400 border-red-800",
  REJECTED_HURDLE: "text-red-400 border-red-800", REJECTED_RISK: "text-red-400 border-red-800",
  REJECTED_PROTECTED: "text-red-400 border-red-800",
  STALE: "text-slate-500 border-slate-700", UNAVAILABLE: "text-slate-500 border-slate-700",
  INSUFFICIENT_EVIDENCE: "text-slate-500 border-slate-700", CASH_REFERENCE: "text-sky-300 border-sky-800",
  OPEN: "text-sky-300 border-sky-800", RESOLVED: "text-slate-400 border-slate-700",
};
const Badge = ({ v }) => (
  <span className={`text-[9px] font-mono border rounded-sm px-1.5 py-0.5 ${STATUS_CLS[v] || "text-slate-400 border-slate-700"}`}>{v}</span>
);
const KV = ({ label, value, testId }) => (
  <div><div className="metric-label">{label}</div>
    <div className="text-slate-200 mt-0.5 font-mono text-xs" data-testid={testId}>{value}</div></div>
);
const fmt$ = (v) => (v === null || v === undefined) ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function IntelligenceArena() {
  const [ladder, setLadder] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [opps, setOpps] = useState([]);
  const [hurdle, setHurdle] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [auction, setAuction] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [doctrines, setDoctrines] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");
  const [evidence, setEvidence] = useState({ subject: "XRP", direction: "strengthening", text: "" });

  const load = useCallback(() => {
    api.get("/cash-engine/ladder").then((r) => setLadder(r.data)).catch(() => {});
    api.get("/cash-engine/productivity").then((r) => setProductivity(r.data)).catch(() => {});
    api.get("/cash-engine/opportunities").then((r) => setOpps(r.data.opportunities)).catch(() => {});
    api.get("/harvester/hurdle").then((r) => setHurdle(r.data)).catch(() => {});
    api.get("/arena/questions?status=OPEN").then((r) => setQuestions(r.data.questions)).catch(() => {});
    api.get("/arena/expertise").then((r) => setExpertise(r.data.expertise)).catch(() => {});
    api.get("/auction/history").then((r) => setAuction(r.data.auctions[0] || null)).catch(() => {});
    api.get("/conviction/ledger").then((r) => setLedger(r.data.theses)).catch(() => {});
    api.get("/auction/balance-sheets").then((r) => setSheets(r.data.strategies)).catch(() => {});
    api.get("/arena/participants").then((r) => setDoctrines(r.data.doctrines)).catch(() => {});
    api.get("/event-expectations/dashboard").then((r) => setEvents(r.data.events)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="intelligence-arena-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100 tracking-tight">Intelligence Arena</h1>
          <p className="text-xs font-mono text-slate-500 mt-1">
            Forecasts earn influence · confidence is calibrated · capital competes · research only — PAPER
          </p>
        </div>
      </div>

      <div className="text-[10px] font-mono border border-slate-800 rounded-sm px-3 py-2 text-slate-500 space-y-0.5" data-testid="arena-doctrines">
        {doctrines.slice(0, 6).map((d, i) => <div key={i}>· {d}</div>)}
      </div>

      <Panel title="Capital Productivity — every dollar justifies where it is" testId="capital-productivity">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KV label="Idle capital" value={fmt$(productivity?.capital_idle_usd)} testId="prod-idle" />
          <KV label="Idle days" value={productivity?.capital_idle_days ?? "—"} testId="prod-idle-days" />
          <KV label="Opportunity cost" value={fmt$(productivity?.opportunity_cost_usd)} testId="prod-opp-cost" />
          <KV label="Capital utilized" value={productivity ? `${productivity.capital_utilized_pct}%` : "—"} testId="prod-utilized" />
          <KV label="Capital protected" value={fmt$(productivity?.capital_protected_usd)} testId="prod-protected" />
          <KV label="Yield earned" value={fmt$(productivity?.yield_earned_usd)} testId="prod-yield" />
        </div>
        <div className="grid md:grid-cols-5 gap-2 mt-4" data-testid="capital-ladder">
          {(ladder?.tiers || []).map((t) => (
            <div key={t.tier} className="border border-slate-800 rounded-sm p-2">
              <div className="text-[9px] font-mono text-amber-500/80">{t.tier} · {t.label}</div>
              <div className="text-sm font-mono text-slate-200 mt-1">{fmt$(t.total_usd)}</div>
              <div className="text-[9px] font-mono text-slate-600 mt-1">{t.buckets.map((b) => b.key).join(", ")}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="text-[10px] font-mono text-slate-500" data-testid="cash-hurdle">
            CASH HURDLE: {hurdle?.hurdle_rate != null ? `${hurdle.hurdle_rate}%` : "INSUFFICIENT EVIDENCE"}
            {hurdle?.components && <span className="text-slate-600"> (cash {hurdle.components.cash_yield} + risk {hurdle.components.risk_premium} + friction {hurdle.components.friction} + uncertainty {hurdle.components.uncertainty_penalty})</span>}
          </div>
          <Button size="sm" variant="outline" data-testid="generate-recommendations-button"
            disabled={busy === "rec"} onClick={() => act("rec", () => api.post("/cash-engine/recommendations", {}), "Paper recommendations generated")}
            className="border-slate-700 text-slate-300 text-[10px] h-7 px-3 font-mono">PAPER RECOMMENDATIONS</Button>
        </div>
        <table className="w-full mt-3 text-[10px] font-mono" data-testid="yield-opportunities">
          <thead><tr className="text-slate-600 text-left">
            <th className="py-1">OPPORTUNITY</th><th>TYPE</th><th>NET APY</th><th>LOCKUP</th><th>RISK</th><th>STATUS</th></tr></thead>
          <tbody>
            {opps.slice(0, 6).map((o) => (
              <tr key={o.opportunity_id} className="border-t border-slate-800/60 text-slate-400">
                <td className="py-1">{o.opportunity_id}</td><td>{o.instrument_type}</td>
                <td>{o.estimated_net_apy}%</td><td>{o.lockup_days}d</td>
                <td>{o.counterparty_risk}</td><td><Badge v={o.status} /></td></tr>))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Arena — open forecast questions" testId="arena-questions">
        <div className="flex gap-2 mb-3">
          <Button size="sm" variant="outline" data-testid="generate-questions-button" disabled={busy === "gen"}
            onClick={() => act("gen", () => api.post("/arena/questions/generate", { cadence: "DAILY" }), "Daily questions generated")}
            className="border-slate-700 text-slate-300 text-[10px] h-7 px-3 font-mono">GENERATE DAILY</Button>
          <Button size="sm" variant="outline" data-testid="resolve-questions-button" disabled={busy === "res"}
            onClick={() => act("res", () => api.post("/arena/resolve", {}), "Resolution sweep complete")}
            className="border-slate-700 text-slate-300 text-[10px] h-7 px-3 font-mono">RESOLVE DUE</Button>
        </div>
        <div className="space-y-1.5" data-testid="questions-list">
          {questions.slice(0, 8).map((q) => (
            <div key={q.id} className="flex items-center justify-between border border-slate-800/60 rounded-sm px-2 py-1.5">
              <span className="text-[10px] font-mono text-slate-300">{q.text}</span>
              <span className="flex items-center gap-2">
                <span className="text-[9px] font-mono text-slate-600">{q.asset || "MARKET"} · {q.horizon_days}d · {q.cadence}</span>
                <Button size="sm" variant="outline" data-testid={`run-question-${q.id}`} disabled={busy === q.id}
                  onClick={() => act(q.id, () => api.post(`/arena/questions/${q.id}/run`, {}), "Participants ran")}
                  className="border-slate-700 text-slate-400 text-[9px] h-6 px-2 font-mono">RUN</Button>
              </span>
            </div>))}
          {questions.length === 0 && <div className="text-[10px] font-mono text-slate-600">no open questions — generation follows evidence, not activity quotas</div>}
        </div>
      </Panel>

      <Panel title="Expertise — who is reliably right about what, under which conditions" testId="expertise-graph">
        <table className="w-full text-[10px] font-mono" data-testid="expertise-table">
          <thead><tr className="text-slate-600 text-left">
            <th className="py-1">PARTICIPANT</th><th>DIMENSION</th><th>KEY</th><th>N</th>
            <th>BRIER</th><th>ACCURACY</th><th>CAL ERR</th><th>INFLUENCE</th></tr></thead>
          <tbody>
            {expertise.slice(0, 12).map((e, i) => (
              <tr key={i} className="border-t border-slate-800/60 text-slate-400">
                <td className="py-1">{e.participant_id}</td><td>{e.dimension}</td><td>{e.key}</td>
                <td>{e.n}</td><td>{e.brier_mean}</td><td>{(e.directional_accuracy * 100).toFixed(0)}%</td>
                <td>{e.calibration_error}</td><td className="text-amber-500/90">{e.influence}</td></tr>))}
          </tbody>
        </table>
        {expertise.length === 0 && <div className="text-[10px] font-mono text-slate-600 mt-2">no resolved forecasts yet — influence is earned from calibration, never granted</div>}
      </Panel>

      <Panel title="Capital Auction — strategies compete for deployable capital" testId="capital-auction">
        <div className="flex gap-2 mb-3">
          <Button size="sm" data-testid="run-auction-button" disabled={busy === "auc"}
            onClick={() => act("auc", () => api.post("/auction/run", {}), "Auction complete (PAPER)")}
            className="bg-slate-100 text-slate-900 hover:bg-white text-[10px] h-7 px-3 font-mono">RUN PAPER AUCTION</Button>
        </div>
        {auction && (
          <div className="space-y-1.5" data-testid="auction-latest">
            <div className="text-[10px] font-mono text-slate-500">
              {auction.created_at?.slice(0, 16).replace("T", " ")} · regime {auction.regime} · awarded {fmt$(auction.capital_awarded_usd)} of {fmt$(auction.deployable_at_auction)} deployable
              {auction.residual_destination && (
                <span data-testid="auction-residual"> · residual {fmt$(auction.residual_capital_usd)} → {auction.residual_destination}{auction.residual_reference_yield ? ` @ ${auction.residual_reference_yield}%` : ""}</span>)}
              {auction.friction && <span data-testid="auction-latency"> · latency {auction.friction.total_decision_latency_ms}ms</span>}
              {" "}· {auction.label}
            </div>
            {auction.evaluated.map((e, i) => (
              <div key={i} className="flex items-center justify-between border border-slate-800/60 rounded-sm px-2 py-1.5">
                <span className="text-[10px] font-mono text-slate-300">
                  {e.source} · {e.asset} · req {fmt$(e.requested_usd)} → award {fmt$(e.awarded_usd)}
                  {e.capital_efficiency_score != null && <span className="text-slate-600"> · score {e.capital_efficiency_score}</span>}
                </span>
                <span className="flex items-center gap-2">
                  {e.reason && <span className="text-[9px] font-mono text-slate-600 max-w-xs truncate">{e.reason}</span>}
                  <Badge v={e.status} />
                </span>
              </div>))}
          </div>)}
        {!auction && <div className="text-[10px] font-mono text-slate-600">no auction yet — capital is scarce internally; strategies must win it</div>}
      </Panel>

      <Panel title="Conviction Ledger — owner thesis vs Hussar evidence (never averaged)" testId="conviction-ledger">
        <div className="grid md:grid-cols-2 gap-2" data-testid="conviction-cards">
          {ledger.map((t) => (
            <div key={t.thesis_id} className="border border-slate-800 rounded-sm p-2.5" data-testid={`conviction-${t.subject}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-slate-200">{t.subject}</span>
                <span className="text-[9px] font-mono text-slate-600">policy impact: {t.policy_impact}</span>
              </div>
              <div className="flex gap-4 mt-1.5 text-[10px] font-mono">
                <span className="text-slate-400">OWNER {t.owner_conviction != null ? `${t.owner_conviction}%` : "STRUCTURAL"}</span>
                <span className="text-amber-500/90">HUSSAR {t.hussar_conviction != null ? `${t.hussar_conviction}%` : "UNRESOLVED"}</span>
                {t.divergence != null && <span className={t.divergence < 0 ? "text-red-400" : "text-emerald-400"}>DIV {t.divergence > 0 ? "+" : ""}{t.divergence} pts</span>}
              </div>
              {t.specialist_tracking && (
                <div className="flex gap-3 mt-1 text-[9px] font-mono text-slate-500">
                  <span>NETWORK {t.network_adoption_score}</span>
                  <span>TOKEN CAPTURE {t.native_token_value_capture_score}</span>
                </div>)}
              <div className="text-[9px] font-mono text-slate-600 mt-1 leading-snug">{t.divergence_note}</div>
            </div>))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3 items-center">
          <select data-testid="evidence-subject" value={evidence.subject}
            onChange={(e) => setEvidence({ ...evidence, subject: e.target.value })}
            className="bg-[#101114] border border-slate-700 text-slate-300 text-[10px] font-mono h-7 px-2 rounded-sm">
            {ledger.map((t) => <option key={t.subject} value={t.subject}>{t.subject}</option>)}
          </select>
          <select data-testid="evidence-direction" value={evidence.direction}
            onChange={(e) => setEvidence({ ...evidence, direction: e.target.value })}
            className="bg-[#101114] border border-slate-700 text-slate-300 text-[10px] font-mono h-7 px-2 rounded-sm">
            <option value="strengthening">strengthening</option>
            <option value="contrary">contrary</option>
            <option value="unresolved">unresolved</option>
          </select>
          <input data-testid="evidence-text" value={evidence.text}
            onChange={(e) => setEvidence({ ...evidence, text: e.target.value })}
            placeholder="evidence + why it matters"
            className="bg-[#101114] border border-slate-700 text-slate-300 text-[10px] font-mono h-7 px-2 rounded-sm flex-1 min-w-[180px]" />
          <Button size="sm" variant="outline" data-testid="add-evidence-button" disabled={busy === "ev" || !evidence.text}
            onClick={() => act("ev", () => api.post(`/conviction/${evidence.subject}/evidence`, evidence), "Evidence recorded")}
            className="border-slate-700 text-slate-300 text-[10px] h-7 px-3 font-mono">ADD EVIDENCE</Button>
        </div>
      </Panel>

      <Panel title="Event Expectations — market-derived probabilities vs Hussar forecasts" testId="event-expectations">
        <div className="text-[9px] font-mono text-slate-600 mb-2 leading-snug" data-testid="event-doctrine">
          Prediction markets provide probabilities. Hussar determines whether those probabilities matter to capital.
          Research-only: no wagering, no event-contract trading, no autonomous capital allocation.
        </div>
        <table className="w-full text-[10px] font-mono" data-testid="event-expectations-table">
          <thead><tr className="text-slate-600 text-left">
            <th className="py-1">EVENT</th><th>CATEGORY</th><th>EXTERNAL</th><th>HUSSAR</th>
            <th>DISAGREEMENT</th><th>QUALITY</th><th>CAPITAL RELEVANCE</th><th>RESOLUTION</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.event_id} className="border-t border-slate-800/60 text-slate-400 align-top"
                  data-testid={`event-row-${e.event_id}`}>
                <td className="py-1 pr-2 max-w-[280px]">
                  <div className="text-slate-200 leading-snug">{e.question}</div>
                  {(e.impacts || []).map((i) => (
                    <div key={i.impact_id} className="text-[9px] text-slate-500" data-testid={`impact-${i.impact_id}`}>
                      {i.asset}: <span className={i.conditional_direction === "POSITIVE" ? "text-emerald-400" : i.conditional_direction === "NEGATIVE" ? "text-red-400" : "text-slate-400"}>{i.conditional_direction}</span>
                      {" · "}{i.expected_magnitude} · conf {Math.round(i.confidence * 100)}%
                    </div>))}
                </td>
                <td className="whitespace-nowrap">{e.event_category.replace(/_/g, " ")}</td>
                <td>{e.external_probabilities.length
                  ? e.external_probabilities.map((x) => (
                    <div key={x.source}>{x.source}: {Math.round(x.p * 100)}%</div>))
                  : "—"}</td>
                <td>{e.hussar_probabilities.length
                  ? e.hussar_probabilities.map((x) => (
                    <div key={x.participant_id}>{x.participant_id.replace("HUSSAR_", "")}: {Math.round(x.p * 100)}%</div>))
                  : "—"}</td>
                <td><Badge v={e.disagreement ? `${e.disagreement.replace(/_/g, " ")}${e.disagreement_pts != null ? ` ${e.disagreement_pts}pts` : ""}` : "—"} /></td>
                <td><Badge v={e.market_quality || "—"} /></td>
                <td><Badge v={e.capital_relevance || "—"} /></td>
                <td>{e.resolution
                  ? <span className={e.resolution.outcome ? "text-emerald-400" : "text-red-400"} data-testid={`event-outcome-${e.event_id}`}>{e.resolution.outcome ? "TRUE" : "FALSE"}</span>
                  : <span className="text-slate-500">{e.status}</span>}</td>
              </tr>))}
          </tbody>
        </table>
        {events.length === 0 && <div className="text-[10px] font-mono text-slate-600 mt-2" data-testid="event-expectations-empty">no event questions registered</div>}
      </Panel>

      <Panel title="Strategy Balance Sheets — return on risk capital" testId="strategy-balance-sheets">
        <table className="w-full text-[10px] font-mono" data-testid="balance-sheet-table">
          <thead><tr className="text-slate-600 text-left">
            <th className="py-1">STRATEGY</th><th>DEPLOYED</th><th>NET VALUE</th><th>FEES</th>
            <th>TAX DRAG</th><th>REGRET</th><th>PROTECTED</th><th>RISK</th><th>RORC</th></tr></thead>
          <tbody>
            {sheets.map((s) => (
              <tr key={s.strategy} className="border-t border-slate-800/60 text-slate-400">
                <td className="py-1">{s.strategy}</td><td>{fmt$(s.capital_deployed_usd)}</td>
                <td>{fmt$(s.net_value_created_usd)}</td><td>{fmt$(s.fees_usd)}</td>
                <td>{fmt$(s.tax_drag_usd)}</td><td>{fmt$(s.decision_regret_usd)}</td>
                <td>{fmt$(s.capital_protected_usd)}</td><td>{fmt$(s.risk_consumed_usd)}</td>
                <td className="text-amber-500/90">{s.return_on_risk_capital ?? "—"}</td></tr>))}
          </tbody>
        </table>
        {sheets.length === 0 && <div className="text-[10px] font-mono text-slate-600 mt-2">no strategy activity yet</div>}
      </Panel>
    </div>
  );
}
